/**
 * The pieces the Well Servicing AI pages draw (WS-6, fieldiq-api#1296): the
 * deterministic figures of each run as rows, the AI draft with its review, and
 * the "Draft with AI" launchers the tour sheet, ticket, job and report pages
 * carry. WT-6's WtAiBits, for this vertical — the same layout and review rules
 * over WS's figures (a tour's hours by billing class, codes, crew, depth and
 * string work; a preflight's billing classes, subsistence and rent-ins; crew
 * certificates, pressure control, the BOP stack check and gauges).
 *
 * THE FIGURES ARE THE API'S DETERMINISTIC LAYER, shown first and in full. The
 * AI wrote only the draft text below them. A pending draft reads "awaiting
 * your approval" and says nothing is published. A run whose narrative is
 * absent still has correct figures, and says the narrative is absent with the
 * API's reason.
 *
 * Every value that did not arrive renders `—`; a real 0 renders `0`.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, Pencil, Sparkles, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { useReviewWsAi, useRunWsAi, useWsAiDigest, type WsAiRunRequest } from '../../../hooks/useWsAi';
import { withUnit, wsBillingSkin, wsDay, wsHours } from '../../../lib/well-servicing';
import { WS_AI_MAX_NARRATIVE, WS_AI_MAX_OBSERVATION, wsAiRefusal, wsAiStateSkin } from '../../../lib/ws-ai';
import type {
  WsAiDraft,
  WsAiRunResult,
  WsComplianceDigestData,
  WsPreflightData,
  WsPreflightRateGap,
  WsReportDraftData,
  WsTourSummaryData,
} from '../../../types/ws-ai';
import { WS_INPUT, WS_PRIMARY, WS_SECONDARY, WsBadge, WsEmpty, WsField, WsFormError, WsPanel, WsSpec } from '../WsBits';

const arr = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
const text = (v: unknown): string => (typeof v === 'string' && v.trim() ? v : '—');
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);

// ─── A small read-only list of rows ──────────────────────────────────────────

interface Col<T> {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  align?: 'right';
}

function AiTable<T>({ rows, cols, rowKey, rail, empty, testId, minWidth = 'min-w-[640px]' }: {
  rows: T[];
  cols: Col<T>[];
  rowKey: (row: T, i: number) => string;
  rail?: (row: T) => string;
  empty: string;
  testId: string;
  minWidth?: string;
}) {
  if (rows.length === 0) return <WsEmpty>{empty}</WsEmpty>;
  return (
    <div className="overflow-x-auto">
      <table className={`w-full ${minWidth} text-left`} data-testid={testId}>
        <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
          <tr className="border-l-4 border-l-transparent">
            {cols.map((c) => (
              <th key={c.key} scope="col" className={`px-4 py-3 ${c.align === 'right' ? 'text-right' : ''}`}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={rowKey(r, i)} data-testid={`${testId}-row`} className={`border-t border-l-4 border-fieldiq-border ${rail ? rail(r) : 'border-l-transparent'}`}>
              {cols.map((c) => (
                <td key={c.key} className={`px-4 py-3 text-sm text-fieldiq-text ${c.align === 'right' ? 'text-right font-mono tabular-nums' : ''}`}>
                  {c.render(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const days = (list: unknown) => {
  const d = arr<string>(list);
  return d.length ? d.map((x) => wsDay(x)).join(', ') : '—';
};

// ─── Tour summary ────────────────────────────────────────────────────────────

export function TourSummaryFigures({ data }: { data: WsTourSummaryData }) {
  const t = data.tour ?? null;
  const classes = Object.entries(data.hoursByBillingClass ?? {});
  const codes = arr<WsTourSummaryData['codes'][number]>(data.codes);
  const crew = arr<WsTourSummaryData['crew'][number]>(data.crew);
  const gaps = arr<WsTourSummaryData['timeline']['gaps'][number]>(data.timeline?.gaps);
  const events = arr<WsTourSummaryData['stringEvents'][number]>(data.stringEvents);
  const tallies = arr<WsTourSummaryData['tallies'][number]>(data.tallies);
  const tests = arr<WsTourSummaryData['pressureTests'][number]>(data.pressureTests);
  const drills = arr<WsTourSummaryData['drills'][number]>(data.drills);
  const unit = data.depthUnit ?? null;
  const title = [t?.jobNumber, t?.workDate ? wsDay(t.workDate) : null].filter((p) => p && p !== '—').join(' · ') || 'Tour summary';
  return (
    <div className="space-y-4" data-testid="ws-ai-tour-figures">
      <WsPanel eyebrow="Tour summary" title={title}>
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
          <WsSpec label="Job" value={t?.jobId ? <Link className="underline" to={`/well-servicing/jobs/${t.jobId}`}>{text(t.jobNumber)}</Link> : '—'} mono />
          <WsSpec label="Tour sheet" value={t?.id ? <Link className="underline" to={`/well-servicing/tours/${t.id}`} data-testid="ws-ai-tour-link">{wsDay(t.workDate)}</Link> : '—'} />
          <WsSpec label="Customer" value={text(t?.customerName)} />
          <WsSpec label="Well" value={text(t?.wellName)} />
          <WsSpec label="Rig" value={text(t?.rigNumber)} />
          <WsSpec label="Tour" value={t?.startTime || t?.endTime ? `${text(t?.startTime)} → ${text(t?.endTime)}` : '—'} />
          <WsSpec label="Tour hours" value={wsHours(data.timeline?.tourHours)} mono />
          <WsSpec label="Covered" value={wsHours(data.timeline?.coveredHours)} mono />
          <WsSpec label="Activities" value={displayValue(num(data.activities))} mono />
          <WsSpec label="Crew hours" value={wsHours(data.crewHours)} mono />
          <WsSpec label="Deepest" value={withUnit(data.depth?.deepest, unit)} mono />
          <WsSpec label="Fuel used" value={withUnit(data.fuelUsedLitres, 'L')} mono />
        </dl>
        {data.depth?.summary && <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">{data.depth.summary}</p>}
      </WsPanel>
      <WsPanel eyebrow="Tour summary" title="Hours by billing class" testId="ws-ai-tour-classes">
        <AiTable
          rows={classes}
          testId="ws-ai-class"
          rowKey={([k]) => k}
          rail={([k]) => wsBillingSkin(k).rail}
          empty="No hours were logged on this tour."
          minWidth="min-w-[360px]"
          cols={[
            { key: 'class', header: 'Billing class', render: ([k]) => <WsBadge skin={wsBillingSkin(k)} value={k} /> },
            { key: 'hours', header: 'Hours', align: 'right', render: ([, h]) => wsHours(h) },
          ]}
        />
      </WsPanel>
      <WsPanel eyebrow="Tour summary" title="Activity codes" testId="ws-ai-tour-codes">
        <AiTable
          rows={codes}
          testId="ws-ai-code"
          rowKey={(c, i) => `${c.code}:${c.billingClass}:${i}`}
          empty="No activity was logged on this tour."
          cols={[
            { key: 'code', header: 'Code', render: (c) => <span className="font-mono font-semibold text-white">{text(c.code)}</span> },
            { key: 'label', header: 'Activity', render: (c) => text(c.label) },
            { key: 'class', header: 'Billing class', render: (c) => <WsBadge skin={wsBillingSkin(c.billingClass)} value={c.billingClass} /> },
            { key: 'hours', header: 'Hours', align: 'right', render: (c) => wsHours(c.hours) },
          ]}
        />
      </WsPanel>
      <WsPanel eyebrow="Tour summary" title="Gaps in the timeline" testId="ws-ai-tour-gaps">
        <AiTable
          rows={gaps}
          testId="ws-ai-gap"
          rowKey={(g, i) => `${g.from}:${i}`}
          rail={() => 'border-l-fi-amber'}
          empty="The activities cover the whole tour."
          minWidth="min-w-[360px]"
          cols={[
            { key: 'from', header: 'From', render: (g) => text(g.from) },
            { key: 'to', header: 'To', render: (g) => text(g.to) },
            { key: 'minutes', header: 'Minutes', align: 'right', render: (g) => displayValue(num(g.minutes)) },
          ]}
        />
      </WsPanel>
      <WsPanel eyebrow="Tour summary" title="Crew" testId="ws-ai-tour-crew">
        <AiTable
          rows={crew}
          testId="ws-ai-crew"
          rowKey={(c, i) => `${c.name}:${i}`}
          empty="No crew was logged on this tour."
          minWidth="min-w-[480px]"
          cols={[
            { key: 'name', header: 'Name', render: (c) => <span className="font-semibold text-white">{text(c.name)}</span> },
            { key: 'position', header: 'Position', render: (c) => text(c.position) },
            { key: 'hours', header: 'Hours', align: 'right', render: (c) => wsHours(c.hours) },
          ]}
        />
      </WsPanel>
      <WsPanel eyebrow="Tour summary" title="String work" testId="ws-ai-tour-string">
        <AiTable
          rows={events}
          testId="ws-ai-string-event"
          rowKey={(e, i) => `${e.action}:${e.kind}:${i}`}
          empty="No string event was logged on this tour."
          cols={[
            { key: 'action', header: 'Action', render: (e) => text(e.action) },
            { key: 'kind', header: 'Item', render: (e) => text(e.kind) },
            { key: 'qty', header: 'Quantity', align: 'right', render: (e) => displayValue(num(e.quantity)) },
            { key: 'length', header: 'Length', align: 'right', render: (e) => text(e.length) },
          ]}
        />
        {tallies.length > 0 && (
          <AiTable
            rows={tallies}
            testId="ws-ai-tally"
            rowKey={(x, i) => `${x.kind}:${x.direction}:${i}`}
            empty=""
            cols={[
              { key: 'kind', header: 'Tally', render: (x) => text(x.kind) },
              { key: 'direction', header: 'Direction', render: (x) => text(x.direction) },
              { key: 'joints', header: 'Joints', align: 'right', render: (x) => displayValue(num(x.joints)) },
              { key: 'length', header: 'Length', align: 'right', render: (x) => withUnit(x.totalLength, x.lengthUnit) },
            ]}
          />
        )}
      </WsPanel>
      <WsPanel eyebrow="Tour summary" title="Pressure tests and drills" testId="ws-ai-tour-tests">
        <AiTable
          rows={tests}
          testId="ws-ai-pressure-test"
          rowKey={(x, i) => `${x.kind}:${i}`}
          empty="No pressure test was logged on this tour."
          minWidth="min-w-[420px]"
          cols={[
            { key: 'kind', header: 'Test', render: (x) => text(x.kind) },
            { key: 'on', header: 'Tested', render: (x) => wsDay(x.testedOn) },
            { key: 'result', header: 'Result', render: (x) => text(x.result) },
          ]}
        />
        {drills.length > 0 && (
          <AiTable
            rows={drills}
            testId="ws-ai-drill"
            rowKey={(x, i) => `${x.kind}:${i}`}
            empty=""
            minWidth="min-w-[420px]"
            cols={[
              { key: 'kind', header: 'Drill', render: (x) => text(x.kind) },
              { key: 'on', header: 'Drilled', render: (x) => wsDay(x.drilledOn) },
              { key: 'secs', header: 'Seconds to secure', align: 'right', render: (x) => displayValue(num(x.secondsToSecure)) },
            ]}
          />
        )}
      </WsPanel>
      <WsPanel eyebrow="From the tour sheet" title="Remarks">
        <p className="whitespace-pre-wrap px-4 py-4 text-sm text-fieldiq-text">{text(data.remarks)}</p>
      </WsPanel>
    </div>
  );
}

// ─── Preflight ───────────────────────────────────────────────────────────────

const READY_SKIN = { label: 'Ready', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' };
const NOT_READY_SKIN = { label: 'Not ready', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' };

const gapCols = (first: string): Col<WsPreflightRateGap>[] => [
  { key: 'label', header: first, render: (g) => <span className="font-semibold text-white">{text(g.label)}</span> },
  { key: 'days', header: 'Days', render: (g) => days(g.days) },
  { key: 'why', header: 'Why', render: (g) => text(g.reason) },
];

export function PreflightFigures({ data }: { data: WsPreflightData }) {
  const unpriced = arr<WsPreflightData['unpricedLines'][number]>(data.unpricedLines);
  const sheets = arr<WsPreflightData['sheetsWithoutTicket'][number]>(data.sheetsWithoutTicket);
  const classes = arr<WsPreflightRateGap>(data.classesWithoutRate);
  const subsistence = arr<WsPreflightRateGap>(data.subsistenceWithoutRate);
  const rentIns = arr<WsPreflightRateGap>(data.rentInsWithoutRate);
  const title = data.ticket?.ticketNumber || (data.job?.jobNumber ? `${data.job.jobNumber} (whole job)` : 'Preflight');
  return (
    <div className="space-y-4" data-testid="ws-ai-preflight-figures">
      <WsPanel eyebrow="Preflight" title={title}>
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-5">
          <div className="min-w-0">
            <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Ready to send</dt>
            <dd className="mt-1" data-testid="ws-ai-preflight-ready">
              {typeof data.ready === 'boolean' ? <WsBadge skin={data.ready ? READY_SKIN : NOT_READY_SKIN} value={String(data.ready)} /> : '—'}
            </dd>
          </div>
          <div className="min-w-0" data-testid="ws-ai-preflight-flags">
            <WsSpec label="Flags" value={displayValue(num(data.flagCount))} mono />
          </div>
          <WsSpec label="Tickets checked" value={displayValue(num(data.ticketsChecked))} mono />
          <WsSpec label="Sheets checked" value={displayValue(num(data.sheetsChecked))} mono />
          <WsSpec label="Currency" value={text(data.currency)} />
        </dl>
      </WsPanel>
      <WsPanel eyebrow="Preflight" title="Unpriced lines" testId="ws-ai-unpriced">
        <AiTable
          rows={unpriced}
          testId="ws-ai-unpriced-line"
          rowKey={(l) => l.lineId}
          rail={() => 'border-l-fi-amber'}
          empty="Every line has a price."
          cols={[
            { key: 'ticket', header: 'Ticket', render: (l) => (l.ticketId ? <Link className="font-mono underline" to={`/well-servicing/tickets/${l.ticketId}`}>{text(l.ticketNumber)}</Link> : '—') },
            { key: 'line', header: 'Line', render: (l) => <span className="font-semibold text-white">{text(l.description)}</span> },
            { key: 'why', header: 'Why', render: (l) => text(l.reason) },
          ]}
        />
      </WsPanel>
      <WsPanel eyebrow="Preflight" title="Approved tour sheets on no ticket" testId="ws-ai-unticketed">
        <AiTable
          rows={sheets}
          testId="ws-ai-unticketed-sheet"
          rowKey={(s) => s.tourId}
          rail={() => 'border-l-fi-amber'}
          empty="Every approved tour sheet on this job is on a ticket."
          minWidth="min-w-[300px]"
          cols={[{ key: 'day', header: 'Work date', render: (s) => <Link className="underline" to={`/well-servicing/tours/${s.tourId}`}>{wsDay(s.workDate)}</Link> }]}
        />
        {data.scope === 'ticket' && sheets.length > 0 && (
          <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">These are the job's other sheets; they do not stop this ticket being sent.</p>
        )}
      </WsPanel>
      <WsPanel eyebrow="Preflight" title="Billing classes with no rate" testId="ws-ai-class-gaps">
        <AiTable rows={classes} testId="ws-ai-class-gap" rowKey={(g) => g.key} rail={() => 'border-l-fi-amber'} empty="Every billing class on the sheets has a rate on the card." cols={gapCols('Billing class')} />
      </WsPanel>
      <WsPanel eyebrow="Preflight" title="Subsistence with no rate" testId="ws-ai-subsistence-gaps">
        <AiTable rows={subsistence} testId="ws-ai-subsistence-gap" rowKey={(g) => g.key} rail={() => 'border-l-fi-amber'} empty="Every subsistence day has a rate on the card." cols={gapCols('Subsistence')} />
      </WsPanel>
      <WsPanel eyebrow="Preflight" title="Rent-ins with no rate" testId="ws-ai-rentin-gaps">
        <AiTable rows={rentIns} testId="ws-ai-rentin-gap" rowKey={(g) => g.key} rail={() => 'border-l-fi-amber'} empty="Every billable rent-in has a billing rate." cols={gapCols('Rent-in')} />
      </WsPanel>
    </div>
  );
}

// ─── Well report draft ───────────────────────────────────────────────────────

/** The report the narrative is for. The report's own figures live on the report itself. */
export function ReportDraftFigures({ data }: { data: WsReportDraftData }) {
  return (
    <WsPanel eyebrow="Well report draft" title="End-of-job report" testId="ws-ai-report-figures">
      <dl className="grid gap-4 px-4 py-4 sm:grid-cols-3">
        <WsSpec label="Job" value={data.job?.id ? <Link className="underline" to={`/well-servicing/jobs/${data.job.id}`}>{text(data.job.jobNumber)}</Link> : '—'} mono />
        <WsSpec label="Report status" value={text(data.report?.status)} />
      </dl>
      <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
        Approving writes this narrative into the report's summary, and only while the report is a draft.
      </p>
    </WsPanel>
  );
}

// ─── Compliance digest ───────────────────────────────────────────────────────

const stateCell = (s: string | null | undefined) => <WsBadge skin={wsAiStateSkin(s)} value={s ?? ''} />;
const jobsCell = (list: unknown) => {
  const j = arr<string>(list);
  return j.length ? j.join(', ') : '—';
};

export function ComplianceFigures({ data }: { data: WsComplianceDigestData }) {
  const crew = arr<WsComplianceDigestData['crewCerts'][number]>(data.crewCerts);
  const pc = arr<WsComplianceDigestData['pressureControl'][number]>(data.pressureControl);
  const stack = arr<WsComplianceDigestData['stackGuard'][number]>(data.stackGuard);
  const gauges = arr<WsComplianceDigestData['gauges'][number]>(data.gauges);
  return (
    <div className="space-y-4" data-testid="ws-ai-compliance-figures">
      <WsPanel eyebrow="Compliance digest" title="Open well-servicing work">
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-3 lg:grid-cols-6">
          <WsSpec label="As of" value={wsDay(data.today)} />
          <WsSpec label="Items" value={displayValue(num(data.total))} mono />
          <WsSpec label="Crew certs" value={displayValue(crew.length)} mono />
          <WsSpec label="Pressure control" value={displayValue(pc.length)} mono />
          <WsSpec label="BOP stack check" value={displayValue(stack.length)} mono />
          <WsSpec label="Gauges" value={displayValue(gauges.length)} mono />
        </dl>
      </WsPanel>
      <WsPanel eyebrow="Compliance" title="Crew certificates" testId="ws-ai-certs">
        <AiTable
          rows={crew}
          testId="ws-ai-cert-item"
          rowKey={(x, i) => `${x.userId}:${x.cert}:${i}`}
          rail={(x) => wsAiStateSkin(x.state).rail}
          empty="Everyone crewed on open work holds current certificates."
          cols={[
            { key: 'name', header: 'Crew member', render: (x) => <span className="font-semibold text-white">{text(x.name)}</span> },
            { key: 'cert', header: 'Certificate', render: (x) => text(x.cert) },
            { key: 'jobs', header: 'Jobs', render: (x) => jobsCell(x.jobNumbers) },
            { key: 'state', header: 'State', render: (x) => stateCell(x.state) },
            { key: 'due', header: 'Expires', render: (x) => wsDay(x.dueOn) },
          ]}
        />
      </WsPanel>
      <WsPanel eyebrow="Compliance" title="Pressure control" testId="ws-ai-pressure-control">
        <AiTable
          rows={pc}
          testId="ws-ai-pc-item"
          rowKey={(x, i) => `${x.equipmentId}:${x.component ?? ''}:${i}`}
          rail={(x) => wsAiStateSkin(x.state).rail}
          empty="No pressure-control component on an open job is expired, due or missing a certificate."
          cols={[
            { key: 'unit', header: 'Unit', render: (x) => <span className="font-semibold text-white">{text(x.label)}</span> },
            { key: 'component', header: 'Component', render: (x) => text(x.component) },
            { key: 'jobs', header: 'Jobs', render: (x) => jobsCell(x.jobNumbers) },
            { key: 'state', header: 'State', render: (x) => stateCell(x.state) },
            { key: 'due', header: 'Due', render: (x) => wsDay(x.dueOn) },
          ]}
        />
      </WsPanel>
      <WsPanel eyebrow="Compliance" title="BOP stack check" testId="ws-ai-stack-guard">
        <AiTable
          rows={stack}
          testId="ws-ai-stack-item"
          rowKey={(x, i) => `${x.equipmentId}:${x.jobNumber ?? ''}:${i}`}
          rail={(x) => wsAiStateSkin(x.state).rail}
          empty="Every BOP stack on an open job passes its check for the job's planned dates."
          cols={[
            { key: 'unit', header: 'Stack', render: (x) => <span className="font-semibold text-white">{text(x.label)}</span> },
            { key: 'job', header: 'Job', render: (x) => text(x.jobNumber) },
            { key: 'end', header: 'Planned end', render: (x) => wsDay(x.plannedEndOn) },
            { key: 'state', header: 'State', render: (x) => stateCell(x.state) },
            { key: 'why', header: 'Why', render: (x) => text(x.error) },
          ]}
        />
      </WsPanel>
      <WsPanel eyebrow="Compliance" title="Gauges" testId="ws-ai-gauges">
        <AiTable
          rows={gauges}
          testId="ws-ai-gauge-item"
          rowKey={(x, i) => `${x.equipmentId}:${i}`}
          rail={(x) => wsAiStateSkin(x.state).rail}
          empty="No gauge is due a calibration."
          minWidth="min-w-[420px]"
          cols={[
            { key: 'unit', header: 'Gauge', render: (x) => <span className="font-semibold text-white">{text(x.label)}</span> },
            { key: 'state', header: 'State', render: (x) => stateCell(x.state) },
            { key: 'due', header: 'Due', render: (x) => wsDay(x.dueOn) },
          ]}
        />
      </WsPanel>
    </div>
  );
}

/** The figures for any run, by agent. Unknown agent or missing data says so rather than guessing. */
export function RunFigures({ agent, data }: { agent: string; data: unknown }) {
  if (!data || typeof data !== 'object') {
    return <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-4 text-sm text-fieldiq-text">The figures for this run were not stored.</p>;
  }
  if (agent === 'ws_tour_summary') return <TourSummaryFigures data={data as WsTourSummaryData} />;
  if (agent === 'ws_ticket_preflight') return <PreflightFigures data={data as WsPreflightData} />;
  if (agent === 'ws_well_report_draft') return <ReportDraftFigures data={data as WsReportDraftData} />;
  if (agent === 'ws_compliance_digest') return <ComplianceFigures data={data as WsComplianceDigestData} />;
  return <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-4 text-sm text-fieldiq-text">This kind of draft is not shown here.</p>;
}

// ─── The AI draft and its review ─────────────────────────────────────────────

/** The heading says where the draft stands. A PENDING draft is waiting on a person and is not published. */
export const WS_DRAFT_HEADING: Record<string, string> = {
  pending: 'Draft — awaiting your approval',
  actioned: 'Approved',
  dismissed: 'Draft — dismissed',
  superseded: 'Draft — replaced by a newer run',
  no_action: 'Draft — no action',
};

function DraftText({ draft, testId }: { draft: WsAiDraft; testId: string }) {
  const obs = arr<string>(draft.observations);
  return (
    <div className="space-y-3" data-testid={testId}>
      <p className="whitespace-pre-wrap" data-testid={`${testId}-narrative`}>{text(draft.narrative)}</p>
      {obs.length > 0 && (
        <ul className="list-disc space-y-1 pl-5" data-testid={`${testId}-observations`}>
          {obs.map((o, i) => (
            <li key={i}>{o}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** A draft with no narrative text in it is absent, the same as a null draft. */
const hasNarrative = (d: WsAiDraft | null | undefined): d is WsAiDraft => !!d && typeof d.narrative === 'string' && d.narrative.trim() !== '';

/**
 * The AI's text. Approve / Edit / Dismiss only on a PENDING run and only for a
 * role that may review (the API refuses anyone else, and refuses a decided run
 * with 409 not_pending). An absent narrative is SAID to be absent, with the
 * API's reason, and a person may still write the narrative and approve that.
 */
export function WsAiDraftPanel({
  runId,
  outcome,
  draft: rawDraft,
  approved,
  approvedEdited,
  aiError,
  canReview,
}: {
  runId: string;
  outcome: string | null | undefined;
  draft: WsAiDraft | null | undefined;
  approved?: WsAiDraft | null;
  approvedEdited?: boolean | null;
  aiError: string | null | undefined;
  canReview: boolean;
}) {
  const review = useReviewWsAi();
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [narrative, setNarrative] = useState('');
  const [observations, setObservations] = useState('');
  const draft = hasNarrative(rawDraft) ? rawDraft : null;
  const pending = outcome === 'pending';
  const heading = (outcome && WS_DRAFT_HEADING[outcome]) || 'Draft';
  const busy = review.isPending;

  const startEdit = () => {
    setError(null);
    setNarrative(draft?.narrative ?? '');
    setObservations(arr<string>(draft?.observations).join('\n'));
    setEditing(true);
  };

  const approve = (edit?: { narrative: string; observations: string[] }) => {
    setError(null);
    review.mutate(
      { id: runId, action: 'approve', ...(edit ? { edit } : {}) },
      {
        onSuccess: () => {
          setEditing(false);
          toast.success('Draft approved');
        },
        onError: (err) => setError(wsAiRefusal(err, 'The draft was not approved.')),
      },
    );
  };

  const dismiss = () => {
    setError(null);
    review.mutate(
      { id: runId, action: 'dismiss' },
      {
        onSuccess: () => {
          setEditing(false);
          toast.success('Draft dismissed');
        },
        onError: (err) => setError(wsAiRefusal(err, 'The draft was not dismissed.')),
      },
    );
  };

  const submitEdit = () => {
    if (!narrative.trim()) {
      setError('Write the summary before approving it.');
      return;
    }
    approve({ narrative, observations: observations.split('\n').map((o) => o.trim()).filter(Boolean) });
  };

  return (
    <section data-testid="ws-ai-draft" className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-blue bg-fieldiq-card">
      <header className="flex flex-wrap items-end justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
        <div>
          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">AI draft</p>
          <h2 className="mt-1 text-base font-extrabold tracking-tight text-white" data-testid="ws-ai-draft-heading">
            {heading}
          </h2>
        </div>
        {canReview && pending && !editing && (
          <div className="flex flex-wrap gap-2" data-testid="ws-ai-review-actions">
            <button type="button" className={WS_SECONDARY} onClick={dismiss} disabled={busy} data-testid="ws-ai-dismiss">
              <XCircle className="size-4" aria-hidden="true" />
              Dismiss
            </button>
            <button type="button" className={WS_SECONDARY} onClick={startEdit} disabled={busy} data-testid="ws-ai-edit">
              <Pencil className="size-4" aria-hidden="true" />
              {draft ? 'Edit, then approve' : 'Write and approve'}
            </button>
            {draft && (
              <button type="button" className={WS_PRIMARY} onClick={() => approve()} disabled={busy} data-testid="ws-ai-approve">
                {busy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="size-4" aria-hidden="true" />}
                Approve
              </button>
            )}
          </div>
        )}
      </header>
      {pending && (
        <p className="border-b border-fieldiq-border bg-fi-signal-amber/10 px-4 py-2 text-xs text-fieldiq-text" data-testid="ws-ai-not-published">
          Not approved yet. Nothing has been sent, filed or published — this draft waits for a person to approve or dismiss it.
        </p>
      )}
      {error && (
        <p role="alert" className="border-b border-fieldiq-border bg-fi-signal-red/10 px-4 py-3 text-sm text-fieldiq-text" data-testid="ws-ai-review-error">
          {error}
        </p>
      )}
      <div className="space-y-3 px-4 py-4 text-sm text-fieldiq-text">
        {editing ? (
          <div className="space-y-3" data-testid="ws-ai-edit-form">
            <WsField label="Summary" hint={`Up to ${WS_AI_MAX_NARRATIVE} characters.`}>
              <textarea
                className={`${WS_INPUT} h-32 py-2`}
                aria-label="Summary"
                maxLength={WS_AI_MAX_NARRATIVE}
                value={narrative}
                onChange={(e) => setNarrative(e.target.value)}
                data-testid="ws-ai-edit-narrative"
              />
            </WsField>
            <WsField label="Observations" hint={`One per line; up to 8, each up to ${WS_AI_MAX_OBSERVATION} characters.`}>
              <textarea
                className={`${WS_INPUT} h-24 py-2`}
                aria-label="Observations"
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                data-testid="ws-ai-edit-observations"
              />
            </WsField>
            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" className={WS_SECONDARY} onClick={() => setEditing(false)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className={WS_PRIMARY} onClick={submitEdit} disabled={busy} data-testid="ws-ai-edit-approve">
                {busy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="size-4" aria-hidden="true" />}
                Approve this text
              </button>
            </div>
          </div>
        ) : outcome === 'actioned' && hasNarrative(approved) ? (
          <>
            <DraftText draft={approved} testId="ws-ai-approved" />
            <p className="text-xs text-fieldiq-muted">{approvedEdited ? 'Edited by the reviewer before approval.' : 'Approved as the AI drafted it.'}</p>
          </>
        ) : draft ? (
          <>
            <DraftText draft={draft} testId="ws-ai-text" />
            <p className="text-xs text-fieldiq-muted">Written by AI from the figures above, as an explanation only. It changes no figure.</p>
          </>
        ) : (
          <div className="space-y-1" data-testid="ws-ai-no-draft">
            <p className="font-bold">AI narrative absent — the figures above are still correct.</p>
            <p className="text-xs text-fieldiq-muted" data-testid="ws-ai-error">
              {aiError ? `Reason: ${aiError}` : 'The API gave no reason.'}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Launchers: "Draft with AI" on the tour, ticket, job and report pages ────

/**
 * A run just made, shown in place. The POST answers without an outcome, so the
 * stored run is read for it (and re-read after a review); until that loads, a
 * fresh run is pending by definition.
 */
export function WsAiRunInPlace({ result, canReview }: { result: WsAiRunResult; canReview: boolean }) {
  const stored = useWsAiDigest(result.agentRunId);
  const s = stored.data;
  return (
    <div className="space-y-4" data-testid="ws-ai-in-place">
      <RunFigures agent={String(result.agent)} data={result.data} />
      <WsAiDraftPanel
        runId={result.agentRunId}
        outcome={s?.outcome ?? 'pending'}
        draft={s ? s.draft : result.draft}
        approved={s?.approved ?? null}
        approvedEdited={s?.approvedEdited ?? null}
        aiError={s ? s.aiError : result.aiError}
        canReview={canReview}
      />
      <p className="text-xs text-fieldiq-muted">
        Kept with the{' '}
        <Link to={`/well-servicing/ai/${result.agentRunId}`} className="font-bold text-fieldiq-text underline" data-testid="ws-ai-run-link">
          AI drafts
        </Link>
        .
      </p>
    </div>
  );
}

/**
 * One "Draft with AI" panel: a button that runs `request`, a refusal in the
 * API's words (a 409 report_issued as a sentence), and the run shown in place.
 * The caller decides whether to render it at all — by role (canUseWsAi) and by
 * the record's state — so a hand who may not call it never sees the button.
 */
export function WsAiLauncher({
  request,
  eyebrow,
  title,
  blurb,
  testId,
}: {
  request: WsAiRunRequest;
  eyebrow: string;
  title: string;
  blurb: string;
  testId: string;
}) {
  const run = useRunWsAi();
  const [result, setResult] = useState<WsAiRunResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const go = () => {
    setError(null);
    run.mutate(request, {
      onSuccess: (r) => setResult(r ?? null),
      onError: (err) => setError(wsAiRefusal(err, 'The AI draft did not run.')),
    });
  };
  return (
    <div className="space-y-4" data-testid={testId}>
      <WsPanel
        eyebrow={eyebrow}
        title={title}
        actions={
          <button type="button" className={WS_SECONDARY} onClick={go} disabled={run.isPending} data-testid={`${testId}-run`}>
            {run.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Sparkles className="size-4" aria-hidden="true" />}
            {run.isPending ? 'Drafting…' : 'Draft with AI'}
          </button>
        }
      >
        <div className="space-y-2 px-4 py-4">
          <WsFormError message={error} />
          <p className="text-xs text-fieldiq-muted">{blurb}</p>
        </div>
      </WsPanel>
      {result && <WsAiRunInPlace result={result} canReview />}
    </div>
  );
}
